/// <amd-module name="Release/Designer/Wizard" />
import * as React from "react";

import { Fabric } from "OfficeFabric/Fabric";
import { Button } from "VSSUI/Button";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";

import { Components, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssHistoryService } from "VSS/Platform/History";
import { Icon } from "VSSUI/Icon";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IReleaseService } from "Release/Client/Service/Release";
import * as RMContracts from "Release/Client/Contracts/Release";

import * as Resources from "Release/Designer/Resources";

interface Props {
}

interface State {
    installationId: string;
    stepNumber: number;
    azureRmconnectionName: string;
    releasePipelineName: string;
    errorMessage: string;
    redirectUrl: string;
}

class DesignerWizardContent extends VssComponent<Props, State, IVssComponentContext> {

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const navigationState = historyService.getState();

        this.state = {
            installationId: navigationState["installationId"],
            stepNumber: 1,
            azureRmconnectionName: "",
            releasePipelineName: "",
            redirectUrl: "",
            errorMessage: ""
        };
    }

    public componentDidMount() {
    }

    public componentWillUnmount() {
    }

    public render(): JSX.Element {
        return (
            <Fabric className="cd-designer-wizard-view">
                <div className="wizard-view">
                <div className="wizard-view-step-list">
                        <div className="header-section">
                            <div className="header">{Resources.NewReleasePipeline}</div>
                            <div className="subheader">{Resources.DeployInSimpleSteps}</div>
                        </div>
                        <ul>
                            <StepListItem index={1} selectedIndex={this.state.stepNumber}>
                                <div className="step-header">{Resources.AzureSubscription}</div>
                                <div className="step-subheader">{this.state.azureRmconnectionName}</div>
                            </StepListItem>
                            <StepListItem index={2} selectedIndex={this.state.stepNumber}>
                                <div className="step-header">{Resources.ReleasePipeline}</div>
                                <div className="step-subheader">{this.state.releasePipelineName}</div>
                            </StepListItem>
                            <StepListItem index={3} selectedIndex={this.state.stepNumber}>
                                <div className="step-header">{Resources.SetupComplete}</div>
                            </StepListItem>                                                   
                        </ul>
                    </div>
                    <div className="wizard-view-detail">
                        {this._renderDetail()}
                    </div>
                </div>
            </Fabric>
        );
    }

    private _renderDetail(): JSX.Element {
        switch (this.state.stepNumber) {
            default:
            case 1:
                return <div className="azurerm-connection-selector">
                    <div className="header-section">
                        <div className="header">{Resources.SelectYourAzureSubscription}</div>
                    </div>
                    { this._getAzureResourceManagerInputComponent() }
                    <Button
                        primary={true}
                        className={css("fabric-style-overrides", "azurerm-connection-continue-button")}
                        disabled={!this.state.azureRmconnectionName}
                        aria-disabled={!this.state.azureRmconnectionName}
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => this._onAzureSubscriptionContinueClick(event)}
                        ariaLabel={Resources.Continue} >
                        {Resources.Continue}
                    </Button>                  
                </div>;
            case 2:
                if (this.state.errorMessage) {
                    return <div className="create-release-pipeline">
                         <div>
                            <div className="header-section">
                                <div className="header">{Resources.FailedToCreateReleasePipeline}</div>
                            </div>
                            <div className="errorMessage">{this.state.errorMessage}</div>
                            <Button
                                primary={true}
                                className={css("fabric-style-overrides", "create-releasepipeline-retry-button")}
                                disabled={false}
                                aria-disabled={false}
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => this._onRetryCreateReleasePipelineClick(event)}
                                ariaLabel={Resources.Retry}>
                                {Resources.Retry}
                            </Button>
                        </div>
                    </div>;
                }
                else {
                    return <div className="create-release-pipeline">
                        <div>
                            <div className="header-section">
                                <div className="header">{Resources.CreatingReleasePipeline}</div>
                            </div>
                            <Spinner size={SpinnerSize.large} />
                        </div>           
                    </div>;
                }
            case 3:
                return <div className="completed-setup">
                    <div>
                        <div className="header-section">
                            <div className="header">{Resources.SetupCompletedSuccessfully}</div>
                            <div className="subheader">{Resources.SetupCompletedMessage}</div>                          
                        </div>
                    </div>               
                    <Button
                        primary={true}
                        className={css("fabric-style-overrides", "setup-completed-button")}
                        disabled={false}
                        aria-disabled={false}
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => this._onSetupCompletedClick(event)}
                        ariaLabel={Resources.Continue}>
                        {Resources.Continue}
                    </Button>
                </div>;
        }
    }

    private _getAzureResourceManagerInputComponent(): JSX.Element {
        const loadingComponent = (<Spinner size={SpinnerSize.medium} />);
        
        return (<LegacyComponent
            modules={["DistributedTaskControls/SharedControls/InputControls/Components/AzureResourceManagerInputComponent"]}
            wrappedType="azureResourceManagerInputComponent"
            loadingComponent={() => loadingComponent}
            wrappedProps={{
                authSchemes: "",
                instanceId: "release-designer-connectedservice",
                label: "",
                value: "",
                onValueChanged: this.onServiceEndPointValueChanged,
                required: false,
                readOnly: false,
                disabled: false,
                forceUpdate: false
            }}
        />);
    }

    private onServiceEndPointValueChanged = (newValue: string) => {
        this.setState({
            azureRmconnectionName: newValue
        });
    }

    private _onRetryCreateReleasePipelineClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        this._onAzureSubscriptionContinueClick(event);
    }

    private _onAzureSubscriptionContinueClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        this.setState({
            errorMessage: "",
            stepNumber: 2
        });

        const releaseService = this.context.pageContext.getService<IReleaseService>("IReleaseService");
        
        const queueIdPromise = this._getEmptyEnvironmentTemplateQueueIdPromise(releaseService);
        
        queueIdPromise.then((queueId: number) => {
            const releaseDefinition: RMContracts.ReleaseDefinition = this._constructReleaseDefinition(queueId);

            releaseService.createReleaseDefinition(releaseDefinition).then((createdReleaseDefinition: RMContracts.ReleaseDefinition) => {
                this._onCreateReleasePipelineComplete(createdReleaseDefinition.name);
            }, (error) => {
                let errorMessage: string = error ? error.message || error : "";
                this.setState({
                    errorMessage: errorMessage
                });
            });
        }, (error) => {
            let errorMessage: string = error ? error.message || error : "";
            this.setState({
                errorMessage: errorMessage
            });
        });
    }

    private _onCreateReleasePipelineComplete(releasePipelineName: string): void {
        const gitHubInstallationUrl = "https://github.com/apps/azure-cd/installations/" + this.state.installationId;

        this.setState({
            stepNumber: 3,
            releasePipelineName: releasePipelineName,
            redirectUrl: gitHubInstallationUrl
        });
    }

    private _onSetupCompletedClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        window.location.href = this.state.redirectUrl;
    }

    private _constructReleaseDefinition(queueId: number): RMContracts.ReleaseDefinition {
        const agentPhaseDeploymentInput: RMContracts.AgentDeploymentInput = {
            queueId: queueId
        } as RMContracts.AgentDeploymentInput;

        const agentPhase: RMContracts.AgentBasedDeployPhase = {
            deploymentInput: agentPhaseDeploymentInput,
            name: "Run on agent",
            refName: "",
            rank: 1,
            workflowTasks: [],
            phaseType: RMContracts.DeployPhaseTypes.AgentBasedDeployment
        };

        let variables: { [key: string]: RMContracts.ConfigurationVariableValue } = {};
        variables["AzureRmConnection"] = {
            value: this.state.azureRmconnectionName,
            isSecret: false,
            allowOverride: false
        };

        let environment: Partial<RMContracts.ReleaseDefinitionEnvironment> = {};
        environment.name = "Deploy";
        environment.rank = 1;
        environment.variables = variables;
        environment.deployPhases = [agentPhase];
        environment.retentionPolicy = {
            daysToKeep: 30,
            releasesToKeep: 3,
            retainBuild: true
        };
        environment.preDeployApprovals = {
            approvalOptions: {} as RMContracts.ApprovalOptions,
            approvals: [
                {
                    isAutomated: true,
                    id: 1,
                    rank: 1
                } as RMContracts.ReleaseApproval
            ]
        };
        environment.postDeployApprovals = {
            approvalOptions: {} as RMContracts.ApprovalOptions,
            approvals: [
                {
                    isAutomated: true,
                    id: 3,
                    rank: 1
                } as RMContracts.ReleaseApproval
            ]
        };

        let definition: Partial<RMContracts.ReleaseDefinition> = {};
        definition.name = "TestRD" + this.state.installationId;
        definition.environments = [];
        definition.environments.push(environment as RMContracts.ReleaseDefinitionEnvironment);

        return definition as RMContracts.ReleaseDefinition;
    }

    private _getEmptyEnvironmentTemplateQueueIdPromise(releaseService: IReleaseService): Promise<number> {
        return releaseService.getDefinitionEnvironmentTemplate("00000000-0000-0000-0000-000000000000").then((template: RMContracts.ReleaseDefinitionEnvironmentTemplate) => {
            if (template.environment) {
                let deployPhase: RMContracts.AgentBasedDeployPhase = template.environment.deployPhases[0] as RMContracts.AgentBasedDeployPhase;
                let deploymentInput: RMContracts.AgentDeploymentInput = deployPhase.deploymentInput as RMContracts.AgentDeploymentInput;
                return deploymentInput.queueId;
            }
            else {
                return 1;
            }
        });
    }
}

interface StepIndicatorProps {
    index: number;
    selectedIndex: number;
    children?: React.ReactNode;
}

const StepListItem = (props: StepIndicatorProps): JSX.Element => {
    const selected: boolean = props.index === props.selectedIndex;
    const completed: boolean = props.index < props.selectedIndex;

    const stepNumber = completed ? <div className="step-number completed"><Icon iconName="CheckMark" /></div> : <div className="step-number">{ props.index }</div>;

    return <li className={css(selected && "selected")}>
        <div className="step-item">
            { stepNumber }
            <div className="step-detail">
                { props.children }
            </div>
        </div>
    </li>
}

Components.add("cd-designer-wizard-view", DesignerWizardContent);
